// components/TransactionUnavailable.tsx
import React from 'react';
import { Card } from '@components/card/card';
import { useTranslation } from 'react-i18next';

type Props = {
  title?: string;
  message?: string;
  hint?: string;

  // Optional context
  transactionType?: string | null;
  operatingUnitName?: string | null;
  inventoryName?: string | null;

  // Actions
  onRetry?: () => void;
  onBack?: () => void;
  onClose?: () => void;

  className?: string;
};

const TransactionUnavailable: React.FC<Props> = ({
  title,
  message,
  hint,
  transactionType,
  operatingUnitName,
  inventoryName,
  onRetry,
  onBack,
  onClose,
  className
}) => {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';

  const defaultTitle =
    title ?? (isAr ? 'الحركة المخزنية غير متاحة حالياً' : 'Inventory transaction is currently unavailable');
  const defaultMsg =
    message ??
    (isAr
      ? 'لا يمكن إنشاء هذه الحركة في الوقت الحالي. قد تكون غير مفعّلة، أو لا توجد صلاحية، أو لا توجد مراجع متاحة.'
      : 'This transaction cannot be created right now. It may be disabled, you may not have permission, or no references are available.');
  const defaultHint =
    hint ??
    (isAr
      ? 'تحقق من الصلاحيات، إعدادات أنواع الحركات، وربط المخزن مع الوحدة التشغيلية ثم حاول مرة أخرى.'
      : 'Check permissions, transaction type setup, and inventory/operating unit mapping, then try again.');

  return (
    <Card className={`border-0 bg-theme bg-opacity-10 ${className ?? ''}`}>
      <div className='p-4 text-center'>
        <div
          className='d-inline-flex align-items-center justify-content-center rounded-circle mb-3'
          style={{ width: 72, height: 72, background: 'rgba(220,53,69,.10)' }}
        >
          <i className='fas fa-ban fs-2 text-danger'></i>
        </div>

        <h5 className='fw-bold mb-2'>{defaultTitle}</h5>
        <p className='text-muted mb-3'>{defaultMsg}</p>

        {(transactionType || operatingUnitName || inventoryName) && (
          <div className='d-flex flex-wrap gap-2 justify-content-center mb-3'>
            {transactionType && (
              <span className='badge bg-secondary'>
                {isAr ? 'النوع' : 'Type'}: {transactionType}
              </span>
            )}
            {operatingUnitName && (
              <span className='badge bg-secondary'>
                {isAr ? 'الوحدة' : 'OU'}: {operatingUnitName}
              </span>
            )}
            {inventoryName && (
              <span className='badge bg-secondary'>
                {isAr ? 'المخزن' : 'Inventory'}: {inventoryName}
              </span>
            )}
          </div>
        )}

        <div className='alert alert-warning d-inline-block text-start mb-3'>
          <i className='fas fa-info-circle me-2'></i>
          {defaultHint}
        </div>

        {(onRetry || onBack || onClose) && (
          <div className='d-flex flex-wrap gap-2 justify-content-center mt-2'>
            {onBack && (
              <button type='button' className='btn btn-outline-secondary' onClick={onBack}>
                <i className='fas fa-arrow-right me-2'></i>
                {isAr ? 'رجوع' : 'Back'}
              </button>
            )}
            {onRetry && (
              <button type='button' className='btn btn-outline-theme' onClick={onRetry}>
                <i className='fas fa-redo me-2'></i>
                {isAr ? 'إعادة المحاولة' : 'Retry'}
              </button>
            )}
            {onClose && (
              <button type='button' className='btn btn-danger' onClick={onClose}>
                <i className='fas fa-times me-2'></i>
                {isAr ? 'إغلاق' : 'Close'}
              </button>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

export default TransactionUnavailable;
